import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, PackageMinus, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "src/shadcn/ui/dialog";
import { EuRelabelCheckInProductRowShortage, RelabelQuantityChangeReason } from "src/types";
import { QUANTITY_CHANGE_REASON_LABEL, QUANTITY_CHANGE_REASON_OPTIONS } from "src/constants/relabelCases";

interface LogShortageModalProps {
  open: boolean;
  onClose: () => void;
  productSkuName: string;
  productName: string;
  expectedQty: number;
  receivedQty: number;
  prevShortageQty: number;
  initialShortage?: EuRelabelCheckInProductRowShortage;
  onSubmit: (input: EuRelabelCheckInProductRowShortage) => void;
  onClear?: () => void;
}

const LogShortageModal: React.FC<LogShortageModalProps> = ({
  open,
  onClose,
  productSkuName,
  productName,
  expectedQty,
  receivedQty,
  prevShortageQty,
  initialShortage,
  onSubmit,
  onClear
}) => {
  const maxShortage = Math.max(0, expectedQty - receivedQty - prevShortageQty);

  const [shortageQty, setShortageQty] = useState<string>("");
  const [reason, setReason] = useState<RelabelQuantityChangeReason | "">("");
  const [reasonNote, setReasonNote] = useState<string>("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setShortageQty(String(initialShortage?.shortageQty ?? maxShortage));
      setReason(initialShortage?.reason ?? "SHORT_DELIVERY");
      setReasonNote(initialShortage?.reasonNote ?? "");
      setTouched(false);
    }
  }, [open, initialShortage, maxShortage]);

  const parsedQty = Number(shortageQty);
  const qtyError = useMemo(() => {
    if (shortageQty.trim() === "") return "Enter a shortage quantity";
    if (!Number.isInteger(parsedQty) || parsedQty < 1) {
      return "Shortage quantity must be a whole number ≥ 1";
    }
    if (parsedQty > maxShortage) {
      return maxShortage <= 0
        ? "No outstanding quantity remains to log a shortage."
        : `Shortage quantity cannot exceed ${maxShortage} (remaining after receipt).`;
    }
    return undefined;
  }, [shortageQty, parsedQty, maxShortage]);

  const reasonError = useMemo(() => (reason ? undefined : "Select a reason"), [reason]);
  const noteError = useMemo(() => {
    if (reason !== "OTHER") return undefined;
    return reasonNote.trim().length === 0 ? "Provide a note" : undefined;
  }, [reason, reasonNote]);

  const canSubmit = !qtyError && !reasonError && !noteError;

  const handleSubmit = () => {
    setTouched(true);
    if (!canSubmit || !reason) return;
    onSubmit({
      shortageQty: parsedQty,
      reason,
      ...(reason === "OTHER" ? { reasonNote: reasonNote.trim() } : {})
    });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-[460px] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-secondary-100">
          <DialogTitle className="flex items-center gap-2.5 text-base font-semibold text-secondary-900">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-amber-500 text-white">
              <PackageMinus className="w-3.5 h-3.5" />
            </span>
            Log Shortage
          </DialogTitle>
          <DialogDescription className="text-xs text-secondary-500 mt-1.5 leading-relaxed">
            Record a shortage against this SKU with a reason — does not block other lines.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-5 space-y-5">
          <div className="rounded-lg border border-secondary-200 bg-secondary-50/50 px-3.5 py-3">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-secondary-400">SKU</span>
              <span className="font-mono text-[11px] font-bold text-secondary-900 bg-white border border-secondary-200 rounded px-1.5 py-0.5">
                {productSkuName}
              </span>
            </div>
            <p className="text-sm text-secondary-700 leading-snug break-words">{productName}</p>
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-secondary-500 mb-2">
              Receipt context
            </label>
            <div className="grid grid-cols-4 gap-3">
              <div className="rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2.5">
                <p className="text-[10px] font-medium uppercase tracking-wider text-secondary-400 leading-none">
                  Expected
                </p>
                <p className="mt-1 text-base font-semibold text-secondary-700 tabular-nums leading-none">
                  {expectedQty}
                </p>
              </div>
              <div className="rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2.5">
                <p className="text-[10px] font-medium uppercase tracking-wider text-secondary-400 leading-none">
                  Receive now
                </p>
                <p className="mt-1 text-base font-semibold text-secondary-700 tabular-nums leading-none">
                  {receivedQty}
                </p>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                <p className="text-[10px] font-medium uppercase tracking-wider text-amber-700 leading-none">
                  Prev. short
                </p>
                <p className="mt-1 text-base font-semibold text-amber-800 tabular-nums leading-none">
                  {prevShortageQty}
                </p>
              </div>
              <div className="rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2.5">
                <p className="text-[10px] font-medium uppercase tracking-wider text-secondary-400 leading-none">
                  Max short
                </p>
                <p className="mt-1 text-base font-semibold text-secondary-700 tabular-nums leading-none">
                  {maxShortage}
                </p>
              </div>
            </div>
          </div>

          <div>
            <label
              htmlFor="log-shortage-qty"
              className="block text-[11px] font-semibold uppercase tracking-wider text-secondary-500 mb-2"
            >
              Shortage quantity
            </label>
            <div
              className={`rounded-lg border bg-white px-3 py-2.5 transition-colors focus-within:ring-1 ${
                touched && qtyError
                  ? "border-error-300 focus-within:border-error-500 focus-within:ring-error-500"
                  : "border-secondary-300 focus-within:border-secondary-900 focus-within:ring-secondary-900"
              }`}
            >
              <input
                id="log-shortage-qty"
                type="number"
                min={1}
                max={maxShortage > 0 ? maxShortage : undefined}
                value={shortageQty}
                onChange={(e) => setShortageQty(e.target.value)}
                onBlur={() => setTouched(true)}
                className="w-full text-base font-semibold text-secondary-900 tabular-nums leading-none bg-transparent border-0 p-0 focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
            {touched && qtyError ? (
              <p className="mt-1.5 text-xs text-error-600">{qtyError}</p>
            ) : (
              <p className="mt-1.5 text-[11px] text-secondary-500">
                {maxShortage > 0
                  ? `Enter any value from 1 to ${maxShortage}.`
                  : "No outstanding quantity available to log a shortage."}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="log-shortage-reason"
              className="block text-[11px] font-semibold uppercase tracking-wider text-secondary-500 mb-2"
            >
              Reason
            </label>
            <div className="relative">
              <select
                id="log-shortage-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value as RelabelQuantityChangeReason | "")}
                onBlur={() => setTouched(true)}
                className={`w-full appearance-none px-3.5 py-2.5 pr-10 text-sm bg-white border rounded-lg focus:outline-none focus:ring-1 transition-colors ${
                  touched && reasonError
                    ? "border-error-300 focus:border-error-500 focus:ring-error-500 text-secondary-900"
                    : "border-secondary-300 focus:border-secondary-900 focus:ring-secondary-900 text-secondary-900"
                }`}
              >
                <option value="" disabled>
                  Select a reason
                </option>
                {QUANTITY_CHANGE_REASON_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {QUANTITY_CHANGE_REASON_LABEL[opt.value]}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
            </div>
            {touched && reasonError && <p className="mt-1.5 text-xs text-error-600">{reasonError}</p>}
          </div>

          {reason === "OTHER" && (
            <div>
              <label
                htmlFor="log-shortage-note"
                className="block text-[11px] font-semibold uppercase tracking-wider text-secondary-500 mb-2"
              >
                Note <span className="text-error-500 normal-case">*</span>
              </label>
              <textarea
                id="log-shortage-note"
                value={reasonNote}
                onChange={(e) => setReasonNote(e.target.value)}
                onBlur={() => setTouched(true)}
                rows={2}
                maxLength={500}
                placeholder="Describe the reason"
                className="w-full px-3.5 py-2.5 text-sm text-secondary-900 placeholder:text-secondary-400 bg-white border border-secondary-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-secondary-900 focus:border-secondary-900 resize-none"
              />
              <div className="flex items-center justify-between mt-1">
                {touched && noteError ? <p className="text-xs text-error-600">{noteError}</p> : <span />}
                <span className="text-[10px] text-secondary-400 tabular-nums">{reasonNote.length}/500</span>
              </div>
            </div>
          )}

          {maxShortage <= 0 && !initialShortage && (
            <div className="flex items-start gap-2 rounded-lg border border-error-200 bg-error-50 p-3">
              <AlertTriangle className="w-4 h-4 text-error-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error-700">
                The full expected quantity has been checked in — there is no outstanding quantity to log as a shortage.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-3.5 border-t border-secondary-100 bg-secondary-50/40 sm:justify-between sm:space-x-2">
          <div>
            {onClear && initialShortage && (
              <button
                type="button"
                onClick={onClear}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-error-700 bg-white border border-error-200 rounded-lg hover:bg-error-50 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Clear shortage
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-secondary-900 rounded-lg hover:bg-black transition-colors disabled:bg-secondary-300 disabled:cursor-not-allowed"
            >
              {initialShortage ? "Update Shortage" : "Log Shortage"}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { LogShortageModal };
